import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Image from '../../../../../../../../components/Image/Image';
import PressCard from '../../../../components/PressCard/PressCard';
import { getInterestName, getPressCardData } from '../../../../utils';
import { StoryCoverCardProps } from './interfaces';
import { getCoverLook } from '../../../../../Lookbook/utils';
import StoryLook from './components/StoryLook/StoryLook';
import { useResize } from '@pages/Root/hooks';
import { getSSRTranslation } from '@/i18n.ts';
import StorySkeletonMayLikeList from '../StorySkeletonMayLikeList/StorySkeletonMayLikeList';
import { getWithOutSpecialChars } from '@pages/Root/utils';
import TextParser from '../../../../../../components/TextParser/TextParser';

import './index.scss';
import { LOOKBOOK_ROUTE_PATH } from '@pages/Root/router/constants.ts';
import { GamAd } from '@moment/components/GamAd/GamAd.tsx';
import { AD_SIZE } from '@moment/components/GalleryAd/ad-constants.ts';
import { AdRenderer } from '@moment/components/AdRenderer/AdRenderer.tsx';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useSearchParams } from 'react-router';
import {
  DISPLAY_AD_DESKTOP_AD_MAP_DATA,
  DISPLAY_AD_MOBILE_AD_MAP_DATA,
  DISPLAY_AD_TABLED_AD_MAP_DATA,
  DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
} from '@moment/components/DisplayAd/constants.ts';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';

const StoryCoverCard: FC<StoryCoverCardProps> = ({
  mayLikeList,
  interestMap,
  lookBook,
}) => {
  const imageLookWrapperRef = useRef<HTMLDivElement>(null);
  const { isDesktop, isMobile } = useResize();
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();
  const context = useContext(CommonPageDataContext);
  const lang = i18n.language;
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const lookList = useMemo(() => {
    if (lookBook) {
      return lookBook.looks.filter(({ uid }) => {
        return uid !== lookBook.coverUID;
      });
    }

    return [];
  }, [lookBook]);

  const coverSlideText = useMemo(() => {
    if (lookBook) {
      const coverLook = lookBook.plan.views.find((view) => {
        return view.panes.some((pane) => {
          return pane.lookUid === lookBook.coverUID;
        });
      });

      const summery = coverLook?.panes.find((item) => {
        return !!item.translations;
      });
      return summery?.translations;
    }

    return {};
  }, [lookBook]);

  const lookBookData = useMemo(() => {
    const look = getCoverLook(lookBook);

    return {
      uid: lookBook.uid,
      title: getSSRTranslation(lookBook?.title, i18n),
      summery: coverSlideText ? getSSRTranslation(coverSlideText, i18n) : '',
      imageURLs: look.thumbnails || [],
      credit: getSSRTranslation(lookBook?.credit, i18n) || '',
      interestName: lookBook.interests
        ? getInterestName(lookBook.interests[0].uid, interestMap, i18n)
        : '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lookBook,
    imageLookWrapperRef,
    imageLookWrapperRef.current,
    interestMap,
    coverSlideText,
  ]);

  const summeryParagraphClass = useMemo(() => {
    return isMobile ? 'paragraph--s' : 'paragraph--m';
  }, [isMobile]);

  const size = [
    ...DISPLAY_AD_DESKTOP_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_MOBILE_AD_MAP_DATA.adSizeList,
  ];

  const responsiveSize = [
    DISPLAY_AD_DESKTOP_AD_MAP_DATA,
    DISPLAY_AD_TABLED_AD_MAP_DATA,
    DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
    DISPLAY_AD_MOBILE_AD_MAP_DATA,
  ];

  // Create GAM ad component
  const adUnitId = 'story';
  const gamAd = (
    <GamAd
      key={`${adUnitId}-gam`}
      adUnitId={adUnitId}
      adType="top"
      size={size}
      responsiveSize={responsiveSize}
      showBanner={true}
      containerClassName="ad--block"
      bannerClassName="ad--title"
      adBlockClassName="feed__ad"
    />
  );

  useEffect(() => {
    if (lookBook) {
      analytics.emitPageViewEvent({
        numInteractions: getCurrentNumberOfInteractions(),
        partnerId: context.partner || '',
        contentId: lookBook.uid,
        source: window.location.href,
        language: lang,
        title: document.querySelector('title')?.text || '',
        contentOwner: lookBook.ownedBy.name,
        primaryInterest: lookBook.interests?.length
          ? lookBook.interests[0].uid
          : '',
        searchParams,
        lbtype: lookBook.lbtype,
      });
    }
  }, [lookBook]);

  const onScroll = useCallback(() => {
    if (!hasScrolled) {
      analytics.emitScrollEvent({
        partnerId: context.partner || '',
        contentId: lookBook.uid,
        source: window.location.href,
        language: lang,
        title: document.querySelector('title')?.text || '',
        contentOwner: lookBook.ownedBy.name,
        primaryInterest: lookBook.interests?.length
          ? lookBook.interests[0].uid
          : '',
        searchParams,
      });
      setHasScrolled(true);
    }
  }, [hasScrolled]);

  useEffect(() => {
    if (lookBook) {
      window.addEventListener('scroll', onScroll);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [lookBook, onScroll]);

  return (
    <div className="story-cover-card">
      <div
        className="story-cover-card__image-wrapper"
        ref={imageLookWrapperRef}
      >
        <Image
          imageContainerClassName="story-cover-card__image"
          imageList={lookBookData.imageURLs}
          alt="lookbook"
        />
      </div>
      <div className="story-cover-card__main">
        <div className="story-cover-card__content">
          <div className="content__text">
            <div className="content__interest label--m">
              {lookBookData.interestName}
            </div>
            <h1 className="content__title">{lookBookData.title}</h1>
            {lookBookData.credit ? (
              <div className="content__credit paragraph--xs">
                <TextParser
                  text={lookBookData.credit}
                  option={{ paragraphClass: 'paragraph--xs' }}
                />
              </div>
            ) : null}
            <div className="content__summery">
              {lookBookData.summery ? (
                <TextParser
                  text={lookBookData.summery}
                  option={{ paragraphClass: summeryParagraphClass }}
                />
              ) : null}
            </div>
          </div>

          <div className="content__look-list">
            <div className="look-list">
              {lookList.map(({ thumbnails, uid }) => {
                const view = lookBook.plan.views.find((item) => {
                  return item.panes.some((item) => {
                    return item.lookUid === uid;
                  });
                });

                const textDate = view?.panes.find((item) => {
                  return item.translations;
                })?.translations;

                const text = textDate ? getSSRTranslation(textDate, i18n) : '';
                const titleStart = text?.indexOf('##');
                const titleEnd = text?.indexOf('\n', titleStart);
                const sectionTitle =
                  titleStart >= 0
                    ? text?.slice(
                        titleStart,
                        titleEnd >= 0 ? titleEnd : text.length
                      )
                    : '';
                const description = text?.replace(sectionTitle, '') || '';

                return (
                  <StoryLook
                    key={uid}
                    title={sectionTitle}
                    description={description}
                    imageList={thumbnails}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className="story-cover-card__recomendation">
          {isDesktop ? (
            <div className="story-cover-card__ad-wrapper">
              <AdRenderer
                key="ad-story-top-key"
                placement="top"
                adUnitId={adUnitId}
                GamComponent={gamAd}
                dtxAdSize={[AD_SIZE.middle]}
                containerClassName="ad--block"
                bannerClassName="ad--title"
                adBlockClassName="feed__ad"
              />
            </div>
          ) : null}

          <section className="recommendation__like-list">
            <div className="like-list">
              <h3 className="like-list__title">{t('youMayLike')}</h3>

              {mayLikeList.length ? (
                mayLikeList.slice(0, 6).map((item) => {
                  const { imageList, title, subtitle, uid } = getPressCardData(
                    item,
                    interestMap,
                    i18n
                  );

                  const link = LOOKBOOK_ROUTE_PATH.replace(
                    ':lookbookId',
                    uid
                  ).replace(':title?', getWithOutSpecialChars(title?.en));

                  return (
                    <PressCard
                      interestId={item.interests[0]}
                      uid={uid}
                      key={uid}
                      link={link}
                      title={getSSRTranslation(title, i18n)}
                      subtitle={subtitle}
                      imageList={imageList}
                    />
                  );
                })
              ) : (
                <StorySkeletonMayLikeList />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default StoryCoverCard;
